import { Injectable, inject } from '@angular/core';
import { ApiService } from '@app/gateway';
import { BroadcastFactory, BroadcastModel, UploadUrlResponse } from '../models';
import { BroadcastRepository } from '../repository';
import { BaseService } from './base';

@Injectable({ providedIn: 'root' })
export class BroadcastService extends BaseService<BroadcastModel> {
  private readonly apiService = inject(ApiService);

  constructor(protected override readonly repository: BroadcastRepository) {
    super(repository);
  }

  public async broadcasts(): Promise<BroadcastModel[]> {
    try {
      const path: string = `${BroadcastFactory.getPath()}/messages`;
      const messages: BroadcastModel[] = await this.apiService.get({ path });
      return messages;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async createBroadcast(data: Partial<BroadcastModel>): Promise<BroadcastModel> {
    try {
      const path: string = `${BroadcastFactory.getPath()}/messages`;
      const { active, ...payload } = data;
      const newBroadcast: BroadcastModel = await this.apiService.post({ path, data: payload });
      return newBroadcast;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async updateBroadcast(id: string, data: Partial<BroadcastModel>): Promise<BroadcastModel> {
    try {
      const path: string = `${BroadcastFactory.getPath()}/messages`;
      const { active, ...payload } = data;
      const payloadWithId = { id, ...payload };
      const updatedBroadcast: BroadcastModel = await this.apiService.post({ path, data: payloadWithId });
      return updatedBroadcast;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async deleteBroadcast(id: string): Promise<void> {
    try {
      const path: string = `${BroadcastFactory.getPath()}/messages/${id}`;
      await this.apiService.delete({ path });
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async activateBroadcast(id: string): Promise<BroadcastModel> {
    try {
      const path: string = `${BroadcastFactory.getPath()}/messages/${id}/activate`;
      const updatedBroadcast: BroadcastModel = await this.apiService.post({ path });
      return updatedBroadcast;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async deactivateBroadcast(id: string): Promise<BroadcastModel> {
    try {
      const path: string = `${BroadcastFactory.getPath()}/messages/${id}/deactivate`;
      const updatedBroadcast: BroadcastModel = await this.apiService.post({ path });
      return updatedBroadcast;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async resendBroadcast(id: string): Promise<BroadcastModel> {
    try {
      const path: string = `${BroadcastFactory.getPath()}/messages/${id}/resend`;
      const updatedBroadcast: BroadcastModel = await this.apiService.post({ path });
      return updatedBroadcast;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async getUploadUrl(fileName: string): Promise<UploadUrlResponse> {
    try {
      const path: string = `${BroadcastFactory.getPath()}/upload`;
      const updatedBroadcast: UploadUrlResponse = await this.apiService.post({ path, data: { fileName } });
      return updatedBroadcast;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }
}
